import { LinkButton } from '@understory-io/leaf/link-button';
import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { localeHrefExternal } from '~/lib/use-locale-href';

export type PrivateEventCtaProps = {
  /**
   * URL `:locale?` segment for building the legacy `/request` link. Pass
   * `params.locale` from the route — `undefined` when the visitor is on the
   * default locale (no prefix in the URL).
   */
  locale: string | undefined;
  /** Heading copy. Defaults to "Are you looking for a private event?" */
  heading?: ReactNode;
  /** Body copy under the heading. */
  body?: ReactNode;
  /** Label inside the primary CTA button. Defaults to "Send request". */
  ctaLabel?: ReactNode;
};

/**
 * Centered call-to-action block inviting visitors to request a private /
 * custom event. Used on the home page and the experience details page.
 * Render gating (storefront preference + shared-experience rules) lives at
 * the call site.
 */
export function PrivateEventCta({
  locale,
  heading,
  body,
  ctaLabel,
}: PrivateEventCtaProps) {
  return (
    <Stack
      as="section"
      gap="xlarge"
      className={css({
        paddingY: { base: '3xlarge', tablet: '[175px]' },
        paddingX: 'medium',
        textAlign: 'center',
      })}
    >
      <Stack
        gap="small"
        align="center"
        className={css({
          maxWidth: '[600px]',
          marginX: 'auto',
        })}
      >
        <Text as="h2" style="subheadline-large">
          {heading ?? (
            <FormattedMessage
              defaultMessage="Are you looking for a private event?"
              description="Default heading of the private event request block"
            />
          )}
        </Text>
        <Text as="p" style="body-large-regular" color="secondary">
          {body ?? (
            <FormattedMessage
              defaultMessage="We can arrange private events for larger groups, such as companies or friend groups. Leave us your details and we'll get back to you as soon as possible."
              description="Default body copy of the private event request block"
            />
          )}
        </Text>
      </Stack>
      <span>
        <LinkButton
          href={localeHrefExternal(locale, '/request')}
          appearance="primary"
          size="large"
        >
          {ctaLabel ?? (
            <FormattedMessage
              defaultMessage="Send request"
              description="Default label for the private event request CTA button"
            />
          )}
        </LinkButton>
      </span>
    </Stack>
  );
}
